import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="glass-card border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <div className="flex items-center gap-3">
            <img 
              src="/tabedlab-logo.png" 
              alt="TabedLab Logo" 
              className="w-10 h-10 object-contain"
            />
            <h1 className="text-xl font-bold text-slate-800">TabedLab</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="glass-card rounded-2xl p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Privacy Policy</h1>
            <p className="text-slate-600">Last Updated: February 6, 2026</p>
          </div>

          <div className="space-y-8 text-slate-700">
            {/* Introduction */}
            <section>
              <p className="text-lg leading-relaxed">
                At TabedLab, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our laboratory data analysis platform.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Database className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900">Information We Collect</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg mb-2">Personal Information</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Name and email address (when you register)</li>
                    <li>Account credentials (encrypted passwords)</li>
                    <li>Profile information</li>
                    <li>Payment information (processed securely via Stripe)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Laboratory Data</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Uploaded files and data sets</li>
                    <li>Analysis results and reports</li>
                    <li>Usage statistics and preferences</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Technical Information</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>IP address and device information</li>
                    <li>Browser type and version</li>
                    <li>Usage data and analytics</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900">How We Use Your Information</h2>
              </div>
              <ul className="list-disc pl-6 space-y-2">
                <li>To provide and maintain our laboratory analysis services</li>
                <li>To process your data and generate analysis reports</li>
                <li>To manage your account and subscription</li>
                <li>To communicate with you about updates and support</li>
                <li>To improve our services and user experience</li>
                <li>To detect and prevent fraud or abuse</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            {/* Data Protection */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Lock className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900">Data Protection & Security</h2>
              </div>
              <div className="space-y-4">
                <p>We implement industry-standard security measures to protect your data:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Encryption:</strong> All data is encrypted in transit (SSL/TLS) and at rest</li>
                  <li><strong>Secure Storage:</strong> Data stored in secure, access-controlled databases</li>
                  <li><strong>Password Protection:</strong> Passwords are hashed using bcrypt</li>
                  <li><strong>Payment Security:</strong> Payments processed via PCI-compliant Stripe</li>
                  <li><strong>Access Control:</strong> Only you can access your analysis data</li>
                  <li><strong>Regular Audits:</strong> Security measures regularly reviewed and updated</li>
                </ul>
              </div>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Data Retention</h2>
              <p className="mb-4">
                We retain your personal information and laboratory data for as long as your account is active or as needed to provide services. You may request deletion of your data at any time by contacting us.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Account data: Retained while account is active</li>
                <li>Analysis data: Retained per your subscription plan</li>
                <li>Payment records: Retained for legal/tax compliance (7 years)</li>
              </ul>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Your Privacy Rights</h2>
              <p className="mb-4">You have the following rights regarding your personal data:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                <li><strong>Data Portability:</strong> Receive your data in a structured format</li>
                <li><strong>Withdraw Consent:</strong> Opt-out of non-essential data processing</li>
                <li><strong>Object:</strong> Object to certain types of data processing</li>
              </ul>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Third-Party Services</h2>
              <p className="mb-4">We use the following trusted third-party services:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Stripe:</strong> Payment processing (PCI-compliant)</li>
                <li><strong>OpenAI:</strong> AI-powered data analysis</li>
                <li><strong>Google OAuth:</strong> Optional authentication service</li>
                <li><strong>MongoDB Atlas:</strong> Secure database hosting</li>
              </ul>
              <p className="mt-4">
                These services have their own privacy policies and security measures. We carefully select partners that maintain high privacy standards.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Cookies & Tracking</h2>
              <p className="mb-4">
                We use essential cookies to maintain your session and provide services. We do not use tracking cookies for advertising purposes.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Session Cookies:</strong> Required for authentication</li>
                <li><strong>Preference Cookies:</strong> Remember your language settings</li>
                <li><strong>Security Cookies:</strong> Protect against fraud</li>
              </ul>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Children's Privacy</h2>
              <p>
                TabedLab is not intended for users under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately.
              </p>
            </section>

            {/* International Users */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">International Data Transfers</h2>
              <p>
                Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy and applicable laws.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. Continued use of our services after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-900">Contact Us</h2>
              </div>
              <p className="mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:
              </p>
              <div className="space-y-2">
                <p className="font-semibold">Email: <a href="mailto:support@tabedlab.com" className="text-blue-600 hover:text-blue-700">support@tabedlab.com</a></p>
                <p className="text-sm text-slate-600">We will respond to your inquiry within 48 hours.</p>
              </div>
            </section>

            {/* GDPR & Compliance */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Compliance</h2>
              <p>
                TabedLab is committed to complying with applicable data protection laws, including GDPR (EU), CCPA (California), and other regional privacy regulations. We respect your privacy rights and ensure lawful processing of your personal data.
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t bg-white/50 backdrop-blur-sm mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center text-slate-600">
          <p>&copy; 2026 TabedLab. All rights reserved.</p>
          <p className="mt-2 text-sm">
            Contact: <a href="mailto:support@tabedlab.com" className="text-blue-600 hover:text-blue-700">support@tabedlab.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;